import React from 'react';
import styled from 'styled-components';
import { theme } from '../../styles/theme';
import { FaHandshake, FaChartLine, FaLightbulb, FaEnvelope, FaLayerGroup, FaCogs } from 'react-icons/fa';

const PageHeader = styled.header`
  background: linear-gradient(to right, rgba(0, 0, 0, 0.85), rgba(0, 0, 0, 0.7));
  height: 40vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 0 ${theme.spacing.md};
  margin-top: 80px;
  text-align: center;

  @media screen and (max-width: 480px) {
    height: 32vh;
    padding: 0 ${theme.spacing.sm};
  }
`;

const PageTitle = styled.h1`
  color: ${theme.colors.secondary};
  font-size: 3rem;
  font-weight: ${theme.typography.fontWeight.bold};
  margin-bottom: ${theme.spacing.sm};

  @media screen and (max-width: 768px) {
    font-size: 2.5rem;
  }

  @media screen and (max-width: 480px) {
    font-size: 2rem;
  }
`;

const PageSubtitle = styled.p`
  color: ${theme.colors.text.primary};
  font-size: 1.2rem;
  max-width: 700px;
  margin: 0 auto;
  line-height: ${theme.typography.lineHeight.body};

  @media screen and (max-width: 480px) {
    font-size: 1rem;
  }
`;

const Section = styled.section`
  padding: 80px 0;
  background: ${({ $alt }) => ($alt ? theme.colors.gradient.dark : theme.colors.gradient.primary)};

  @media screen and (max-width: 768px) {
    padding: 60px 0;
  }

  @media screen and (max-width: 480px) {
    padding: 45px 0;
  }
`;

const SectionInner = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  padding: 0 ${theme.spacing.md};

  @media screen and (max-width: 480px) {
    padding: 0 ${theme.spacing.sm};
  }
`;

const SectionHeading = styled.h2`
  font-size: 2rem;
  color: ${theme.colors.secondary};
  margin-bottom: ${theme.spacing.md};
  display: flex;
  align-items: center;
  gap: 12px;

  @media screen and (max-width: 480px) {
    font-size: 1.6rem;
  }
`;

const LeadParagraph = styled.p`
  font-size: 1.1rem;
  color: ${theme.colors.text.primary};
  line-height: ${theme.typography.lineHeight.body};
  margin-bottom: ${theme.spacing.md};
`;

const Paragraph = styled.p`
  font-size: 1rem;
  color: ${theme.colors.text.primary};
  line-height: ${theme.typography.lineHeight.body};
  margin-bottom: ${theme.spacing.sm};
`;

const List = styled.ul`
  margin: 0 0 ${theme.spacing.md} 1.25rem;
  padding: 0;
`;

const ListItem = styled.li`
  font-size: 1rem;
  color: ${theme.colors.text.primary};
  line-height: ${theme.typography.lineHeight.body};
  margin-bottom: 0.35rem;
`;

const Highlight = styled.span`
  font-weight: ${theme.typography.fontWeight.semiBold};
  color: ${theme.colors.secondary};
`;

const Subheading = styled.h3`
  font-size: 1.3rem;
  color: ${theme.colors.text.primary};
  margin: ${theme.spacing.md} 0 ${theme.spacing.xs};
`;

const EmailLink = styled.a`
  color: ${theme.colors.secondary};
  text-decoration: none;
  font-weight: bold;

  &:hover {
    text-decoration: underline;
  }
`;

const IconWrapper = styled.span`
  color: ${theme.colors.secondary};
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const Investments = () => {
  return (
    <>
      <PageHeader>
        <PageTitle>Afrotech Investments</PageTitle>
        <PageSubtitle>
          Investing in Africa's Future — One Startup at a Time
        </PageSubtitle>
      </PageHeader>

      <Section>
        <SectionInner>
          <LeadParagraph>
            <Highlight>Afrotech Investments</Highlight> is the venture-building and strategic technology investment arm of Afrotech Group.
            We partner with early-stage entrepreneurs who have strong ideas but lack the technical resources to bring them to life.
          </LeadParagraph>
          <Paragraph>
            Instead of traditional cash funding, our investment model focuses on practical technology and execution support that helps
            founders launch faster and reduce risk.
          </Paragraph>
          <Subheading>What we provide:</Subheading>
          <List>
            <ListItem>Software development</ListItem>
            <ListItem>Cloud hosting &amp; infrastructure</ListItem>
            <ListItem>IT support &amp; maintenance</ListItem>
            <ListItem>System architecture &amp; security</ListItem>
            <ListItem>App development (mobile &amp; web)</ListItem>
            <ListItem>Digital transformation consulting</ListItem>
          </List>
          <Paragraph>
            We exchange these services for equity, partnership, or long-term revenue share, depending on the startup's needs.
          </Paragraph>
        </SectionInner>
      </Section>

      <Section $alt>
        <SectionInner>
          <SectionHeading>
            <IconWrapper><FaLightbulb /></IconWrapper> Our Investment Model
          </SectionHeading>
          <Paragraph>
            Afrotech Investments operates as a <Highlight>Venture Studio</Highlight> — meaning we act as a co-founder to help build companies from the ground up, rather than just funding them.
          </Paragraph>
          <Paragraph>We invest in startups by providing:</Paragraph>
          <Subheading>1. Technical Development</Subheading>
          <Paragraph>We build the platform, app, or system required to launch the startup.</Paragraph>

          <Subheading>2. Technology Infrastructure</Subheading>
          <Paragraph>We offer cloud hosting, security, API integrations, and DevOps support.</Paragraph>

          <Subheading>3. Business Strategy</Subheading>
          <Paragraph>
            We help founders with market research, pricing models, branding, and go-to-market strategy.
          </Paragraph>

          <Subheading>4. Support &amp; Scaling</Subheading>
          <Paragraph>
            We stay involved, helping the startup grow, update their systems, and expand.
          </Paragraph>
        </SectionInner>
      </Section>

      <Section>
        <SectionInner>
          <SectionHeading>
            <IconWrapper><FaHandshake /></IconWrapper> Who We Partner With
          </SectionHeading>
          <Paragraph>We invest in early-stage founders in sectors such as:</Paragraph>
          <List>
            <ListItem>Technology</ListItem>
            <ListItem>Security &amp; Asset Tracking</ListItem>
            <ListItem>Logistics</ListItem>
            <ListItem>Bookings &amp; Services Apps</ListItem>
            <ListItem>Media &amp; Publishing</ListItem>
            <ListItem>Fintech</ListItem>
            <ListItem>AI &amp; Automation</ListItem>
            <ListItem>E-commerce</ListItem>
            <ListItem>Health &amp; Lifestyle Tech</ListItem>
          </List>
          <Paragraph>
            If you have a strong idea, you can join our portfolio even with:
          </Paragraph>
          <List>
            <ListItem>No capital</ListItem>
            <ListItem>No technical skills</ListItem>
            <ListItem>No developers</ListItem>
            <ListItem>Just the vision</ListItem>
          </List>
        </SectionInner>
      </Section>

      <Section $alt>
        <SectionInner>
          <SectionHeading>
            <IconWrapper><FaChartLine /></IconWrapper> How Startups Benefit
          </SectionHeading>
          <Paragraph>
            A startup receives all the tech they need without paying upfront:
          </Paragraph>
          <List>
            <ListItem>Professional website</ListItem>
            <ListItem>Mobile or web app</ListItem>
            <ListItem>Backend systems</ListItem>
            <ListItem>Databases</ListItem>
            <ListItem>User management</ListItem>
            <ListItem>Payments integration</ListItem>
            <ListItem>Hosting &amp; cloud support</ListItem>
            <ListItem>Ongoing updates</ListItem>
          </List>
          <Paragraph>
            This dramatically reduces startup risk and increases the likelihood of success.
          </Paragraph>
        </SectionInner>
      </Section>

      <Section>
        <SectionInner>
          <SectionHeading>
            <IconWrapper><FaCogs /></IconWrapper> Partnership Structure
          </SectionHeading>
          <Paragraph>We offer flexible partnership models:</Paragraph>
          <List>
            <ListItem>
              <Highlight>Equity Partnership</Highlight> — We take a percentage share (e.g., 10%–40%) depending on development effort.
            </ListItem>
            <ListItem>
              <Highlight>Revenue Share</Highlight> — A portion of revenue for a fixed period.
            </ListItem>
            <ListItem>
              <Highlight>Joint Venture</Highlight> — We co-build and co-own the company.
            </ListItem>
            <ListItem>
              <Highlight>Technology-for-Equity Swap</Highlight> — We build while founders focus on business execution.
            </ListItem>
          </List>
        </SectionInner>
      </Section>

      <Section $alt>
        <SectionInner>
          <SectionHeading>
            <IconWrapper><FaLayerGroup /></IconWrapper> Current Portfolio (Examples)
          </SectionHeading>
          <Paragraph>
            Afrotech Investments powers the development of:
          </Paragraph>
          <List>
            <ListItem>
              <Highlight>DataFind</Highlight> — Asset Tracking &amp; Recovery Platform
            </ListItem>
            <ListItem>
              <Highlight>Beauty at Home</Highlight> — On-demand beauty service booking
            </ListItem>
            <ListItem>
              <Highlight>CloseUp News</Highlight> — Digital news &amp; journalism platform
            </ListItem>
            <ListItem>New platforms in development (2025–2026)</ListItem>
          </List>
        </SectionInner>
      </Section>

      <Section>
        <SectionInner>
          <SectionHeading>
            <IconWrapper><FaEnvelope /></IconWrapper> Pitch Your Startup
          </SectionHeading>
          <Paragraph>
            If you believe your idea has potential, apply for partnership with Afrotech Investments.
          </Paragraph>
          <Paragraph>
            Email: <EmailLink href="mailto:investments@afrotechgroup.co.za">investments@afrotechgroup.co.za</EmailLink>
          </Paragraph>
          <Paragraph>Use the subject line: <Highlight>Startup Investment Application</Highlight></Paragraph>
          <Paragraph>Include:</Paragraph>
          <List>
            <ListItem>Brief business idea</ListItem>
            <ListItem>Problem you solve</ListItem>
            <ListItem>Target market</ListItem>
            <ListItem>What stage you are at</ListItem>
            <ListItem>What you need Afrotech to build for you</ListItem>
          </List>
          <Paragraph>
            We review early-stage concepts and high-potential founders on a rolling basis.
          </Paragraph>
        </SectionInner>
      </Section>
    </>
  );
};

export default Investments;
